import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import {Link} from 'components/DSYS/Link';
import {formatShortDate} from 'helpers/dates';

import {type DocumentSource, documentSource} from './aiQueryReadDocument';

/**
 * Steps that find or read documents. Their rows are documents whose excerpts
 * and bodies are for the model, so an opened step lists them as titled links
 * (like Dash's cited sources) instead of a table of text, ids and urls.
 */
export const DOC_TOOLS = new Set([
  'search_docs',
  'read_document',
  'find_knowledge_articles',
  'get_knowledge_article_detail',
  'search_guides',
  'search_planning_documents',
]);

type StepDoc = {title: string; url: string | null; meta: string[]};

const SOURCE_NAMES: Record<DocumentSource, string> = {
  confluence: 'Confluence',
  paper: 'Paper',
  drive: 'Google Drive',
  help_center: 'Dropbox Help Center',
  dropbox: 'Dropbox',
};

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

/** Each row's title, link, and what tells it apart: where it lives, its KB number, when it changed. */
export const stepDocs = (items: Array<Record<string, unknown>>): StepDoc[] =>
  items.flatMap((item) => {
    const url = text(item.url) ?? text(item.planning_url) ?? null;
    const title = text(item.title) ?? url;
    if (!title) {
      return [];
    }
    // Only docs Dash found say where they live; a guide's CompanyOS link isn't "Dropbox".
    const source = item.source ? documentSource(item.source, url) : undefined;
    const updated = formatShortDate(
      text(item.updated_at) ?? text(item.updated_on) ?? text(item.document_updated_at)
    );
    const meta = [
      source ? SOURCE_NAMES[source] : undefined,
      text(item.kb_number),
      updated || undefined,
      text(item.error),
    ].filter((part): part is string => Boolean(part));
    return [{title, url, meta}];
  });

/** The documents a step found or read, one linked title per line. */
export const StepDocs = ({docs}: {docs: StepDoc[]}) => (
  <Stack gap="4">
    {docs.map((doc, i) => (
      <Text key={i} size="xsmall" tagName="div">
        {doc.url ? (
          <Link to={doc.url} openInNewTab rel="noopener noreferrer" style={{fontSize: 'inherit'}}>
            {doc.title}
          </Link>
        ) : (
          doc.title
        )}
        {doc.meta.length > 0 ? (
          <Text size="xsmall" color="faint">
            {' · '}
            {doc.meta.join(' · ')}
          </Text>
        ) : null}
      </Text>
    ))}
  </Stack>
);
